import { useState, useEffect } from "react";
import { Dialog } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/styles";
import { DatePickerProps } from "@material-ui/pickers";
import { Close } from "@material-ui/icons";
import {
  ButtonCustom,
  CancelButton,
  defaultMaterialTheme,
} from "../Style/Style";
import { setUTCDateFormate } from "../Utils/UTCDateFormate/setUTCDateFormate";
import {
  CloseButton,
  DateRangeContent,
  DateRangeInput,
  DateTimeRangeInput,
  DateRangeTitle,
  DateRangeButtonsWrapper,
} from "./DateRangePicker.style";

interface IDateRangePickerProps {
  setDateRange(newDateRange: IDateRange): void;
  open: boolean;
  onClose(): void;
  withTime?: boolean;
}

export interface IDateRange {
  startDate: Date | string;
  endDate: Date | string;
}

export const initialDateRange = {
  startDate: undefined,
  endDate: undefined,
};

export const DateRangePicker = ({
  open,
  onClose,
  setDateRange,
  withTime,
}: IDateRangePickerProps) => {
  const [dates, setDates] = useState<IDateRange>(initialDateRange);
  const [lastSaveDates, setLastSaveDates] =
    useState<IDateRange>(initialDateRange);

  useEffect(() => {
    if (open) {
      setLastSaveDates({
        startDate: setUTCDateFormate(dates.startDate, withTime),
        endDate: setUTCDateFormate(dates.endDate, withTime),
      });
    }
  }, [dates.endDate, dates.startDate, open, withTime]);

  const saveHandler = (): void => {
    setDateRange({
      startDate: setUTCDateFormate(dates.startDate, withTime),
      endDate: setUTCDateFormate(dates.endDate, withTime),
    });
    onClose();
  };

  const resetHandler = (): void => {
    setDates({
      startDate: lastSaveDates.startDate,
      endDate: lastSaveDates.endDate,
    });

    setDateRange(initialDateRange);
  };

  const closeHandler = (): void => {
    setDateRange(initialDateRange);
    onClose();
  };

  const baseStartDateProps: DatePickerProps = {
    variant: "inline",
    autoOk: true,
    format: withTime ? "dd.MM.yyyy HH:mm" : "dd.MM.yyyy",
    label: "Початок періоду",
    value: dates.startDate,
    maxDateMessage: "Дата початку пізніше дати кінця",
    maxDate: dates.endDate,
    onChange: (value: Date | string) =>
      setDates({ ...dates, startDate: value }),
  };

  const baseEndDateProps: DatePickerProps = {
    variant: "inline",
    autoOk: true,
    format: withTime ? "dd.MM.yyyy HH:mm" : "dd.MM.yyyy",
    label: "Кінець періоду",
    value: dates.endDate,
    minDateMessage: "Дата кінця раніше дати початку",
    minDate: dates.startDate,
    onChange: (value: Date | string) => setDates({ ...dates, endDate: value }),
  };

  return (
    <Dialog open={open}>
      <CloseButton onClick={closeHandler}>
        <Close fontSize="small" />
      </CloseButton>

      <DateRangeTitle>Виберіть діапазон дат</DateRangeTitle>

      <DateRangeContent>
        <ThemeProvider theme={defaultMaterialTheme}>
          {withTime ? (
            <>
              <DateTimeRangeInput ampm={false} {...baseStartDateProps} />
              <DateTimeRangeInput ampm={false} {...baseEndDateProps} />
            </>
          ) : (
            <>
              <DateRangeInput {...baseStartDateProps} />
              <DateRangeInput {...baseEndDateProps} />
            </>
          )}
        </ThemeProvider>
      </DateRangeContent>

      <DateRangeButtonsWrapper>
        <CancelButton onClick={resetHandler}>Скинути</CancelButton>
        <ButtonCustom onClick={saveHandler}>Зберегти</ButtonCustom>
      </DateRangeButtonsWrapper>
    </Dialog>
  );
};
